<script lang="ts" setup>
import _ from 'lodash';
import { ref, type PropType, toRaw, onMounted, onBeforeUnmount } from 'vue';
import { AppErrorType, ApplicationError, CaseListActionInterface, CaseListActionParams, CaseListColumn, CaseListServiceInterface, CaseServiceInterface, ContainerTypes, Contexts, CurrentContextService, getContainer, getContext, NamedBindings, ReadonlyCaseList, TranslationService, VariableCaseListColumn } from '@dandelion/core';
import CaseListHeadComponent from './CaseListHeaderComponent.vue';
import CaseListRowComponent from './CaseListRowComponent.vue';
import NonPaginatedCaseListComponent from './NonPaginatedCaseListComponent.vue';
import PaginatedCaseList from './PaginatedCaseListComponent.vue';
import PermaDeletionDialog from '../Dialogs/PermaDeletionDialogComponent.vue';
import { OpenPermaDeletionDialogRequested } from '@dandelion/core';
import { ApplicationEvents } from '@dandelion/core';
import { RestoreCaseRequested } from '@dandelion/core';
import { isCaseWithError } from '@dandelion/core';

const props = defineProps({
  caseListColumns: {
    type: Array as PropType<Array<CaseListColumn>>,
    required: false,
    default: () => new Array<CaseListColumn>(),
  },
  context: {
    type: String as PropType<string>,
    required: false,
    default: Contexts.DEFAULT,
  },
  additionalActions: {
    type: Array as PropType<Array<CaseListActionInterface>>,
    required: false,
    default: () => [],
  },
  addDefaultActions: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: true,
  },
  commonPagination: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
  numberOfElementsPerPage: {
    type: Number as PropType<number>,
    required: false,
    default: 20,
  },
  openOnLineClick: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
});

const container = getContainer(props.context);
const caseListService = container.get(NamedBindings.CaseListService) as CaseListServiceInterface;
const caseService = container.get(NamedBindings.CaseService) as CaseServiceInterface;
const currentContext = container.get(NamedBindings.CurrentContextService) as CurrentContextService;
const ts = container.get(NamedBindings.TranslationService) as TranslationService;

const deletedCaseList: ReadonlyCaseList = caseListService.slicedDeletedCases;
const showDeletedCases = ref(false);
const permaDeletionDialogVisibility = ref(false);
const deletingCaseID = ref('');

const defaultActions = [
  {
    name: 'restoreCase',
    actionClass: 'restore-case',
    label: 'Restore Case',
    action: (values: CaseListActionParams) =>
      getContext(values.rowProps.context).dispatchEvent(
        ApplicationEvents.RestoreCaseRequested,
        new RestoreCaseRequested(ContainerTypes.DEFAULT, values.element.caseMetaData.caseID)
      ),
  },
  {
    name: 'deletePermanently',
    actionClass: 'delete-permanently',
    label: 'Delete permanently',
    action: (values: CaseListActionParams) =>
      getContext(values.rowProps.context).dispatchEvent(
        ApplicationEvents.OpenPermaDeletionDialogRequested,
        new OpenPermaDeletionDialogRequested(ContainerTypes.DEFAULT, values.element.caseMetaData.caseID)
      ),
  },
];
const arrayOfActions: Array<CaseListActionInterface> = [
  ...props.additionalActions,
  ...(props.addDefaultActions ? defaultActions : []),
];

const cachedVariables = currentContext.getCaseConfiguration().cachedVariables;
const caseListVariables = toRaw(props.caseListColumns)
  .filter((column) => column instanceof VariableCaseListColumn)
  .map((column) => (column as VariableCaseListColumn).getVariableName());

// if there are columns that are not cached, throw an error
if (_.intersection(caseListVariables, cachedVariables).length < caseListVariables.length) {
  throw new ApplicationError('All columns must be cached in case configuration', AppErrorType.COLUMNS_NOT_CACHED);
}

caseListService.startDeletedList.value = 0;
caseListService.endDeletedList.value = props.numberOfElementsPerPage;

let tokenForRestoreListener: string | null = null;
let tokenForPermaListener: string | null = null;
onMounted(() => {
  tokenForRestoreListener = getContext(props.context).addEventListener(
    ApplicationEvents.RestoreCaseRequested,
    async (_, event) => {
      const caseID = (event as RestoreCaseRequested).caseID;
      const eventDate = new Date();
      await caseService.restoreCase(caseID);
      await caseListService.restoreCase(caseID, eventDate);
    }
  );

  tokenForPermaListener = getContext(props.context).addEventListener(
    ApplicationEvents.OpenPermaDeletionDialogRequested,
    (_, event) => {
      deletingCaseID.value = (event as OpenPermaDeletionDialogRequested).caseID;
      permaDeletionDialogVisibility.value = true;
    }
  );
});

onBeforeUnmount(() => {
  if (tokenForRestoreListener) {
    getContext(props.context).removeEventListenerByToken(tokenForRestoreListener);
  }
  if (tokenForPermaListener) {
    getContext(props.context).removeEventListenerByToken(tokenForPermaListener);
  }
});

function closePermaDeletionDialog() {
  permaDeletionDialogVisibility.value = false;
}

const isUpdatingCaseList = caseListService.isUpdatingCaseList();

async function updateCurrentPage(value: { startIndex: number; endIndex: number }) {
  caseListService.startDeletedList.value = value.startIndex;
  caseListService.endDeletedList.value = value.endIndex;
  await caseListService.updateCaseList();
}
</script>

<template>
  <div class="deleted-case-list-wrapper">
    <PermaDeletionDialog
      :context="context"
      :deleting-case-i-d="deletingCaseID"
      :perma-deletion-dialog-visibility="permaDeletionDialogVisibility"
      @close-perma-deletion="closePermaDeletionDialog"
    />

    <button
      type="button"
      @click="showDeletedCases = !showDeletedCases"
      :class="`${showDeletedCases ? 'hide-deleted-cases' : 'show-deleted-cases'}-btn`"
      v-html="ts.t(showDeletedCases ? 'Hide deleted cases' : 'Show deleted cases')"
    />

    <section v-if="showDeletedCases" class="deleted-case-list">
      <section v-if="isUpdatingCaseList" class="loading-deleted-case-list">
        <span v-html="ts.t('Loading deleted case list...')" />
      </section>
      <section v-else-if="deletedCaseList.length === 0" class="no-cases-deleted">
        <span v-html="ts.t('No cases deleted')" />
      </section>
      <table v-else class="deleted-case-list-table">
        <CaseListHeadComponent :case-list-columns="caseListColumns" />
        <tbody>
          <CaseListRowComponent
            v-for="(row, rowIndex) in deletedCaseList"
            :row-index="rowIndex"
            :key="isCaseWithError(row) ? row.caseID : row.caseMetaData.caseID"
            :case-list-columns="caseListColumns"
            :context="context"
            :actions="arrayOfActions"
            :open-on-line-click="true"
            :is-deleted-case-list="true"
          />
        </tbody>
      </table>
      <template v-if="!commonPagination">
        <NonPaginatedCaseListComponent
          :context="context"
          :number-of-elements-per-page="numberOfElementsPerPage"
          :deleted-case-list="true"
          @update="updateCurrentPage"
        />
      </template>
      <template v-if="commonPagination">
        <PaginatedCaseList
          :context="context"
          :number-of-elements-per-page="numberOfElementsPerPage"
          :deleted-case-list="true"
          @update="updateCurrentPage"
        />
      </template>
    </section>
  </div>
</template>
